import { api } from "./client.js";
import type {
  Member,
  MemberSignupEntry,
  OnlineMember,
  DisciplinaryAction,
  DisciplinaryActionPublic,
  DisciplinaryActionList,
  LoaRequest,
  CreateDaBody,
  PatchDaBody,
  CreateLoaBody,
  PatchLoaBody,
} from "@wwrp/shared";

/** Fields editable via PATCH /members/{id}. joinDate is excluded (sheet-managed). */
export interface PatchMemberBody {
  referredByMemberId?: number | null;
  websiteId?: string | null;
  firstNameLastInitial?: string | null;
  email?: string | null;
  pinNumber?: string | null;
  steamHex?: string | null;
  ts3Uid?: string | null;
  websiteProfileUrl?: string | null;
  communityStatus?: string | null;
  communityPosition?: string | null;
}

export interface MembersPage {
  items: Member[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListMembersParams {
  role?: string;
  status?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export const membersApi = {
  listDisciplinaryActions: (number = "", limit = 25, offset = 0) => {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (number) qs.set("number", number);
    return api.get<DisciplinaryActionList>(`/disciplinary-actions?${qs}`);
  },
  me: () => api.get<Member>("/members/me"),
  mySignupEntries: () => api.get<MemberSignupEntry[]>("/members/me/signup-entries"),

  myDisciplinaryActions: () =>
    api.get<DisciplinaryActionPublic[]>("/members/me/disciplinary-actions"),

  myLoaRequests: () => api.get<LoaRequest[]>("/members/me/loa-requests"),

  submitMyLoaRequest: (body: { startDate: string; endDate: string; reason: string }) =>
    api.post<LoaRequest>("/members/me/loa-requests", body),

  list: (params: ListMembersParams = {}) => {
    const qs = new URLSearchParams();
    if (params.role) qs.set("role", params.role);
    if (params.status) qs.set("status", params.status);
    if (params.q) qs.set("q", params.q);
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    const query = qs.toString();
    return api.get<MembersPage>(`/members${query ? `?${query}` : ""}`);
  },

  get: (id: number) => api.get<Member>(`/members/${id}`),
  getSignupEntries: (id: number) => api.get<MemberSignupEntry[]>(`/members/${id}/signup-entries`),

  getByWebsiteId: (websiteId: string) =>
    api.get<Member>(`/members/by-website-id/${encodeURIComponent(websiteId)}`),

  listOnline: () => api.get<OnlineMember[]>("/members/online"),

  patch: (id: number, body: PatchMemberBody) => api.patch<Member>(`/members/${id}`, body),

  getDisciplinaryActions: (id: number) =>
    api.get<DisciplinaryAction[]>(`/members/${id}/disciplinary-actions`),

  createDisciplinaryAction: (id: number, body: CreateDaBody) =>
    api.post<DisciplinaryAction>(`/members/${id}/disciplinary-actions`, body),

  patchDisciplinaryAction: (id: number, daId: number, body: PatchDaBody) =>
    api.patch<DisciplinaryAction>(`/members/${id}/disciplinary-actions/${daId}`, body),

  deleteDisciplinaryAction: (id: number, daId: number) =>
    api.delete<{ deleted: true }>(`/members/${id}/disciplinary-actions/${daId}`),

  getLoaRequests: (id: number) => api.get<LoaRequest[]>(`/members/${id}/loa-requests`),

  createLoaRequest: (id: number, body: CreateLoaBody) =>
    api.post<LoaRequest>(`/members/${id}/loa-requests`, body),

  patchLoaRequest: (id: number, loaId: number, body: PatchLoaBody) =>
    api.patch<LoaRequest>(`/members/${id}/loa-requests/${loaId}`, body),

  deleteLoaRequest: (id: number, loaId: number) =>
    api.delete<{ deleted: true }>(`/members/${id}/loa-requests/${loaId}`),
};
